import { Card, Fade, Skeleton, useTheme } from "@mui/material";
import { useMemo } from "react";

import MDBox from "@/components/atoms/MDBox";
import MDTypography from "@/components/atoms/MDTypography";

import { DashboardColor, pulseSx, surfaceCardSx } from "../styles";

type Props = {
  title: string;
  labels: string[];
  values: number[];
  color?: DashboardColor;
  emptyLabel: string;
  isLoading?: boolean;
  isRefreshing?: boolean;
};

const WIDTH = 560;
const HEIGHT = 200;
const PADDING_X = 12;
const PADDING_TOP = 16;
const PADDING_BOTTOM = 26;

const AreaChartCard = ({
  title,
  labels,
  values,
  color = "info",
  emptyLabel,
  isLoading = false,
  isRefreshing = false,
}: Props) => {
  const theme = useTheme();
  const stroke = (theme.palette as any)[color]?.main ?? theme.palette.primary.main;
  const gradientId = `areaGradient-${color}-${title.replace(/\W/g, "")}`;

  const { linePath, areaPath, points, maxValue } = useMemo(() => {
    const max = Math.max(...values, 1);
    const usableWidth = WIDTH - PADDING_X * 2;
    const usableHeight = HEIGHT - PADDING_TOP - PADDING_BOTTOM;
    const step = values.length > 1 ? usableWidth / (values.length - 1) : 0;

    const coords = values.map((value, index) => ({
      x: PADDING_X + step * index,
      y: PADDING_TOP + usableHeight - (value / max) * usableHeight,
      value,
    }));

    const line = coords.map((point, index) => `${index === 0 ? "M" : "L"} ${point.x} ${point.y}`).join(" ");
    const area = coords.length
      ? `${line} L ${coords[coords.length - 1].x} ${HEIGHT - PADDING_BOTTOM} L ${coords[0].x} ${
          HEIGHT - PADDING_BOTTOM
        } Z`
      : "";

    return { linePath: line, areaPath: area, points: coords, maxValue: max };
  }, [values]);

  const hasData = values.length > 0;

  return (
    <Card sx={surfaceCardSx(color)}>
      <MDBox p={2} pb={0.75}>
        <MDTypography variant="button" fontWeight="bold" display="block">
          {title}
        </MDTypography>
      </MDBox>
      {isLoading ? (
        <MDBox px={2.5} pb={2.5}>
          <Skeleton variant="rounded" height="12rem" />
        </MDBox>
      ) : !hasData ? (
        <MDBox px={2.5} pb={2.5}>
          <MDTypography variant="button" color="text">
            {emptyLabel}
          </MDTypography>
        </MDBox>
      ) : (
        <Fade in timeout={520}>
          <MDBox px={1.75} pb={1.75} sx={pulseSx(isRefreshing)}>
            <svg
              viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
              width="100%"
              height="11rem"
              preserveAspectRatio="none"
              role="img"
              aria-label={title}
            >
              <defs>
                <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={stroke} stopOpacity="0.45" />
                  <stop offset="100%" stopColor={stroke} stopOpacity="0" />
                </linearGradient>
              </defs>
              {[0, 0.5, 1].map((ratio) => (
                <line
                  key={ratio}
                  x1={PADDING_X}
                  x2={WIDTH - PADDING_X}
                  y1={PADDING_TOP + (HEIGHT - PADDING_TOP - PADDING_BOTTOM) * ratio}
                  y2={PADDING_TOP + (HEIGHT - PADDING_TOP - PADDING_BOTTOM) * ratio}
                  stroke={theme.palette.grey[200]}
                  strokeWidth="1"
                />
              ))}
              <path d={areaPath} fill={`url(#${gradientId})`} opacity={0.9} />
              <path
                d={linePath}
                fill="none"
                stroke={stroke}
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{
                  strokeDasharray: 2000,
                  strokeDashoffset: 0,
                  animation: "operationalDraw 1.2s ease-out",
                }}
              />
              <style>{`@keyframes operationalDraw { from { stroke-dashoffset: 2000; } to { stroke-dashoffset: 0; } }`}</style>
              {points.map((point, index) => (
                <circle key={`${labels[index]}-${index}`} cx={point.x} cy={point.y} r="3" fill={stroke}>
                  <title>{`${labels[index]}: ${point.value}`}</title>
                </circle>
              ))}
              {points.map((point, index) =>
                index % Math.ceil(points.length / 8 || 1) === 0 ? (
                  <text
                    key={`label-${index}`}
                    x={point.x}
                    y={HEIGHT - 8}
                    textAnchor="middle"
                    fontSize="11"
                    fill={theme.palette.text.secondary}
                  >
                    {labels[index]}
                  </text>
                ) : null
              )}
            </svg>
            <MDBox display="flex" justifyContent="flex-end">
              <MDTypography variant="caption" color="text">
                máx. {maxValue}
              </MDTypography>
            </MDBox>
          </MDBox>
        </Fade>
      )}
    </Card>
  );
};

export default AreaChartCard;
